import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy,
  DollarSign,
  User,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Wallet,
  CreditCard,
  Shield,
  ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const JoinGamePage = ({ user, onShowAuth }) => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [walletStatus, setWalletStatus] = useState(null);
  const [claimResult, setClaimResult] = useState(null);

  useEffect(() => {
    loadInvite();
  }, [inviteCode]);

  useEffect(() => {
    if (user && invite) {
      checkWalletStatus();
    }
  }, [user, invite]);

  const loadInvite = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cash-game/invite/${inviteCode}`);
      setInvite(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invite not found');
    } finally {
      setLoading(false);
    }
  };

  const checkWalletStatus = async () => {
    try {
      const res = await axios.get(`${API}/cash-game/wallet-status`);
      setWalletStatus(res.data);
    } catch (err) {
      console.error('Error checking wallet status:', err);
    }
  };

  const handleClaim = async () => {
    if (!user) {
      toast.info('Please sign in to join this game');
      onShowAuth?.();
      return;
    }

    try {
      setClaiming(true);
      const res = await axios.post(`${API}/cash-game/invite/${inviteCode}/claim`);
      
      if (res.data.success) {
        if (res.data.already_claimed) {
          toast.info('You already joined this game');
        } else {
          toast.success(res.data.message);
        }
        // Navigate to the game
        const gameRoutes = {
          skins: '/play/skins',
          match_play: '/play/match-play',
          stroke_play: '/play/stroke-play'
        };
        navigate(`${gameRoutes[res.data.game_type]}?event=${res.data.event_id}`);
      } else if (res.data.wallet_setup_required) {
        setClaimResult(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join game');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto mb-3" />
          <p className="text-gray-600">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invite Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-emerald-500 hover:bg-emerald-600">
            Go to Par Seeker
          </Button>
        </div>
      </div>
    );
  }

  // Wallet setup required view
  if (claimResult?.wallet_setup_required) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white p-4">
        <div className="max-w-md mx-auto pt-12">
          <div className="bg-white rounded-3xl p-8 shadow-lg text-center mb-6">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wallet className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Wallet Setup Required</h1>
            <p className="text-gray-600 mb-6">
              Complete your Golf Wallet setup to join <strong>{invite.event_name}</strong>
            </p>

            {invite.is_cash_game && (
              <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-emerald-700">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-semibold">
                    Entry Fee: ${invite.entry_fee >= 100 ? (invite.entry_fee / 100).toFixed(0) : invite.entry_fee.toFixed(0)}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-3 text-left mb-6">
              {claimResult.missing_requirements.map((req, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    {req.includes('rules') ? <Shield className="w-4 h-4 text-amber-600" /> :
                     req.includes('age') ? <User className="w-4 h-4 text-amber-600" /> :
                     <CreditCard className="w-4 h-4 text-amber-600" />}
                  </div>
                  <span className="text-gray-700">{req}</span>
                </div>
              ))}
            </div>

            <Button 
              onClick={() => navigate('/wallet')}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 text-lg rounded-xl"
            >
              <span className="flex items-center justify-center gap-2">
                Set Up Wallet
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </div>

          <p className="text-center text-gray-500 text-sm">
            After setup, return here to join the game
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white p-4">
      <div className="max-w-md mx-auto pt-12">
        {/* Invite Card */}
        <div className="bg-white rounded-3xl p-8 shadow-lg text-center mb-6">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-10 h-10 text-emerald-600" />
          </div>
          
          <p className="text-gray-500 mb-1">You&apos;ve been invited by</p>
          <h2 className="text-xl font-bold text-gray-900 mb-4">{invite.inviter_name}</h2>
          
          <h1 className="text-2xl font-bold text-emerald-600 mb-2">{invite.event_name}</h1>
          
          <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full text-gray-600 mb-6">
            <span className="capitalize">{invite.game_type.replace('_', ' ')}</span>
          </div>

          {invite.is_cash_game && (
            <div className="bg-gradient-to-r from-emerald-500 to-green-500 rounded-2xl p-5 mb-6 text-white">
              <p className="text-emerald-100 text-sm mb-1">Entry Fee</p>
              <p className="text-4xl font-bold">
                ${invite.entry_fee >= 100 ? (invite.entry_fee / 100).toFixed(0) : invite.entry_fee.toFixed(0)}
              </p>
              <p className="text-emerald-100 text-sm mt-1">Cash Prize Pool</p>
            </div>
          )}

          {!user ? (
            <div className="space-y-3">
              <Button 
                onClick={onShowAuth}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 text-lg rounded-xl"
              >
                Sign In to Join
              </Button>
              <p className="text-gray-500 text-sm">
                New to Par Seeker? Create a free account!
              </p>
            </div>
          ) : (
            <Button 
              onClick={handleClaim}
              disabled={claiming}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 text-lg rounded-xl"
            >
              {claiming ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Join Game
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          )}
        </div>

        {/* Wallet Status (if logged in) */}
        {user && walletStatus && invite.is_cash_game && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3">Wallet Status</h3>
            <div className="space-y-2">
              <StatusRow done={walletStatus.rules_accepted} label="Rules accepted" />
              <StatusRow done={walletStatus.age_verified} label="Age verified (18+)" />
              <StatusRow done={walletStatus.payment_method_added} label="Payment card added" />
            </div>
            
            {!walletStatus.can_join_cash_games && (
              <Button 
                onClick={() => navigate('/wallet')}
                variant="outline"
                className="w-full mt-4"
              >
                Complete Wallet Setup
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusRow = ({ done, label }) => (
  <div className="flex items-center gap-3">
    {done ? (
      <CheckCircle className="w-5 h-5 text-emerald-500" />
    ) : (
      <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
    )}
    <span className={done ? 'text-gray-600' : 'text-gray-400'}>{label}</span>
  </div>
);

export default JoinGamePage;
